import { useState, useEffect, useCallback } from 'react';
import {
  ensurePushServiceWorkerRegistered,
  isIosDevice,
  isStandaloneWebApp,
} from '../lib/webPushClient';

export interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

const BANNER_DISMISSED_KEY = 'jobmatch_pwa_install_banner_dismissed';

// Global module-scoped state so beforeinstallprompt is captured even before React finishes mounting
let globalDeferredPrompt: BeforeInstallPromptEvent | null = null;
let globalGuideModalOpen = false;
let globalSwRegistered = false;

function notifyPWAStateChanged() {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new CustomEvent('jobmatch:pwa-state-changed'));
  } catch {
    // Ignore
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e: Event) => {
    e.preventDefault();
    globalDeferredPrompt = e as BeforeInstallPromptEvent;
    notifyPWAStateChanged();
  });

  window.addEventListener('appinstalled', () => {
    globalDeferredPrompt = null;
    globalGuideModalOpen = false;
    notifyPWAStateChanged();
  });

  // Register /sw.js on load so PWA installability criteria are met for all visitors
  const registerSwOnBoot = () => {
    void ensurePushServiceWorkerRegistered().then((reg) => {
      if (reg) {
        globalSwRegistered = true;
        notifyPWAStateChanged();
      }
    });
  };

  if (document.readyState === 'complete') {
    registerSwOnBoot();
  } else {
    window.addEventListener('load', registerSwOnBoot, { once: true });
  }
}

export function isAndroidDevice(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /Android/i.test(navigator.userAgent || '');
}

export function isSafariOnIOS(): boolean {
  if (!isIosDevice() || typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return /Safari/i.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/i.test(ua);
}

export function openPWAInstallGuideModal(): void {
  globalGuideModalOpen = true;
  notifyPWAStateChanged();
}

export function closePWAInstallGuideModal(): void {
  globalGuideModalOpen = false;
  notifyPWAStateChanged();
}

export function usePWAInstall() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(
    () => globalDeferredPrompt
  );
  const [isStandalone, setIsStandalone] = useState<boolean>(() => isStandaloneWebApp());
  const [isIOS, setIsIOS] = useState<boolean>(() => isIosDevice());
  const [isAndroid, setIsAndroid] = useState<boolean>(() => isAndroidDevice());
  const [isOnline, setIsOnline] = useState<boolean>(() =>
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );
  const [swRegistered, setSwRegistered] = useState<boolean>(() => globalSwRegistered);
  const [isGuideModalOpen, setIsGuideModalOpen] = useState<boolean>(() => globalGuideModalOpen);
  const [isBannerDismissed, setIsBannerDismissed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    try {
      return window.sessionStorage.getItem(BANNER_DISMISSED_KEY) === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    const syncState = () => {
      setDeferredPrompt(globalDeferredPrompt);
      setIsStandalone(isStandaloneWebApp());
      setIsIOS(isIosDevice());
      setIsAndroid(isAndroidDevice());
      setSwRegistered(globalSwRegistered);
      setIsGuideModalOpen(globalGuideModalOpen);
    };

    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    const mediaQuery =
      typeof window !== 'undefined' && window.matchMedia
        ? window.matchMedia('(display-mode: standalone)')
        : null;

    window.addEventListener('jobmatch:pwa-state-changed', syncState);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    if (mediaQuery && mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', syncState);
    }

    syncState();

    return () => {
      window.removeEventListener('jobmatch:pwa-state-changed', syncState);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (mediaQuery && mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', syncState);
      }
    };
  }, []);

  const triggerInstall = useCallback(async (): Promise<'accepted' | 'dismissed' | 'guide_opened'> => {
    if (globalDeferredPrompt) {
      try {
        await globalDeferredPrompt.prompt();
        const choice = await globalDeferredPrompt.userChoice;
        if (choice.outcome === 'accepted') {
          globalDeferredPrompt = null;
          globalGuideModalOpen = false;
          notifyPWAStateChanged();
          return 'accepted';
        }
        return 'dismissed';
      } catch {
        openPWAInstallGuideModal();
        return 'guide_opened';
      }
    }

    openPWAInstallGuideModal();
    return 'guide_opened';
  }, []);

  const dismissBanner = useCallback(() => {
    setIsBannerDismissed(true);
    if (typeof window !== 'undefined') {
      try {
        window.sessionStorage.setItem(BANNER_DISMISSED_KEY, '1');
      } catch {
        // Ignore storage error
      }
    }
  }, []);

  return {
    isStandalone,
    isIOS,
    isAndroid,
    isOnline,
    swRegistered,
    hasNativeInstallPrompt: Boolean(deferredPrompt),
    isInstallable: !isStandalone,
    isBannerDismissed,
    isGuideModalOpen,
    triggerInstall,
    openGuideModal: openPWAInstallGuideModal,
    closeGuideModal: closePWAInstallGuideModal,
    dismissBanner,
  };
}
